import type { FormEventHandler, Ref } from 'react';
import Button from 'components/shared/Buttons/BaseButton';
import { CalculatorInput } from 'components/Widget/CalculatorInput/CalculatorInput';
import type { CurrencyFieldProps } from 'components/Widget/shared/useCurrencyFields';
import type { CalculatorFieldErrors } from 'components/Widget/shared/types';
import { EMERGENCY_FUND_SECTIONS } from './constants';

interface EmergencyFundCalculatorFormProps {
  /** Focus target after leaving results. */
  ref?: Ref<HTMLFormElement>;
  errors: CalculatorFieldErrors;
  fieldProps: CurrencyFieldProps;
  onCalculate: () => void;
  onReset: () => void;
}

export const EmergencyFundCalculatorForm = ({
  ref,
  errors,
  fieldProps,
  onCalculate,
  onReset,
}: EmergencyFundCalculatorFormProps) => {
  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    onCalculate();
  };

  return (
    <form
      ref={ref}
      tabIndex={-1}
      aria-label="Emergency fund calculator"
      className="flex flex-col gap-8"
      onSubmit={handleSubmit}>
      {EMERGENCY_FUND_SECTIONS.map(section => (
        <fieldset key={section.key}>
          {/* Legacy shows no group headings; naming the groups keeps them announced without adding copy. */}
          <legend className="sr-only">{section.legend}</legend>

          <div className="grid gap-6 sm:grid-cols-2">
            {section.fields.map(field => (
              // Native validation would pre-empt the calculator's custom range rules.
              <CalculatorInput key={field.label} {...fieldProps(field, errors[field.label])} />
            ))}
          </div>
        </fieldset>
      ))}

      <div className="flex flex-col gap-4 sm:flex-row">
        <Button type="submit">Calculate</Button>
        <Button type="button" variant="outline" onClick={onReset}>
          Reset
        </Button>
      </div>
    </form>
  );
};
